import React, { useEffect, useMemo, useState } from "react";
import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { TEAM_INFO } from "../lib/teamMap";
import { useTeamLookup } from "../lib/teamLookup";
import { TeamBadge } from "./TeamBadge";
import { ALL_NCAA_SCHOOLS } from "../lib/ncaaData";
import { matchNcaaSchool, isKnownNcaaSchool, saveSchoolAlias } from "../lib/ncaaSchoolMatch";
import { PasteFromSheets, tsvToObjects } from "./admin/PasteFromSheets";
import { getSettings } from "../lib/api";
import { pwhlStatCols, computePwhlDerived, fmtDerived } from "../lib/pwhlStatColumns";
import { groupBySeasonOrdered, sumSkater, sumGoalie, sumByCols } from "../lib/seasonsAgg";

// Same season constants as Admin.jsx
const OLYMPIC_YEARS_RANGE = [2030, 2026, 2022, 2018, 2014, 2010];

const STAT_SEASON_OPTIONS = {
    pwhl: ["2030-31", "2029-30", "2028-29", "2027-28", "2026-27", "2025-26", "2024-25", "2024"],
    ncaa: (() => {
        const out = [];
        for (let y = 2030; y >= 2010; y--) out.push(`${y}-${String(y + 1).slice(2)}`);
        return out;
    })(),
    international: OLYMPIC_YEARS_RANGE.map(String),
    other: (() => {
        const out = [];
        for (let y = 2030; y >= 2008; y--) out.push(`${y}-${String(y + 1).slice(2)}`);
        return out;
    })(),
};

const PWHL_TEAM_IDS = Object.keys(TEAM_INFO);
const TEAM_FIRST_SEASON = { sea: "2025-26", van: "2025-26", det: "2026-27", ham: "2026-27", sj: "2026-27", lvs: "2026-27" };
const teamEligibleForSeason = (id, season, expansionNow = false) => {
    if (expansionNow) return true;
    const first = TEAM_FIRST_SEASON[id];
    if (!first) return true;
    return !season || season >= first;
};

const NCAA_SCHOOL_OPTIONS = ALL_NCAA_SCHOOLS.map(({ school, conference }) => ({
    value: school,
    label: `${school} (${conference})`,
}));

const inputCls = "bg-[#0A0B10] border border-[#1E2233] focus:border-[#9D4CDD] outline-none text-white px-3 py-2 rounded-md text-sm transition-colors w-full";

const SKATER_REG_COLS = [
    { key: "gp", label: "GP" }, { key: "g", label: "G" }, { key: "a", label: "A" },
    { key: "p", label: "PTS" }, { key: "pim", label: "PIM" }, { key: "plus_minus", label: "+/-" },
];
const SKATER_PO_COLS = [
    { key: "po_gp", label: "PO GP" }, { key: "po_g", label: "PO G" }, { key: "po_a", label: "PO A" },
    { key: "po_p", label: "PO PTS" }, { key: "po_pim", label: "PO PIM" }, { key: "po_plus_minus", label: "PO +/-" },
];
const GOALIE_REG_COLS = [
    { key: "gp", label: "GP" }, { key: "w", label: "W" }, { key: "l", label: "L" },
    { key: "t", label: "T" }, { key: "otl", label: "OTL" }, { key: "gaa", label: "GAA", step: "0.01" },
    { key: "sv_pct", label: "SV%", step: "0.001" }, { key: "so", label: "SO" },
];
const GOALIE_PO_COLS = [
    { key: "po_gp", label: "PO GP" }, { key: "po_w", label: "PO W" }, { key: "po_l", label: "PO L" },
    { key: "po_t", label: "PO T" }, { key: "po_otl", label: "PO OTL" }, { key: "po_gaa", label: "PO GAA", step: "0.01" },
    { key: "po_sv_pct", label: "PO SV%", step: "0.001" }, { key: "po_so", label: "PO SO" },
];

const formatStat = (v, step) => {
    if (v == null || v === 0) return step ? Number(0).toFixed(step === "0.01" ? 2 : 3) : 0;
    if (step === "0.01") return Number(v).toFixed(2);
    if (step === "0.001") return Number(v).toFixed(3);
    return v;
};

// ---------- Team Cell (per league) ----------
const TeamCell = ({ league, row, idx, onChange, dataTestPrefix, expansionNow, readOnly }) => {
    const { pwhl, ncaa } = useTeamLookup();
    if (readOnly) {
        // Locked / auto-synced rows: show the team as read-only text (logo +
        // ABBREVIATION for PWHL/NCAA; plain name for free-text leagues).
        const info = league === "ncaa" ? ncaa(row.team) : league === "pwhl" ? pwhl(row.team) : null;
        const abbr = info?.abbr || row.team || "—";
        const showBadge = (league === "pwhl" || league === "ncaa") && row.team;
        return (
            <span className="inline-flex items-center gap-1 text-xs py-1 whitespace-nowrap" data-testid={`${dataTestPrefix}-team`} title={info?.name || row.team}>
                {showBadge ? <TeamBadge teamId={row.team} size="sm" asLink={false} /> : null}
                <span className="text-[#E2E8F0] font-bold">{abbr}</span>
            </span>
        );
    }
    if (league === "pwhl") {
        return (
            <select
                value={row.team ?? ""}
                onChange={(e) => onChange(idx, "team", e.target.value)}
                className={inputCls + " text-xs py-1 min-w-[100px]"}
                data-testid={`${dataTestPrefix}-team`}
            >
                <option value="">—</option>
                {PWHL_TEAM_IDS
                    .filter((id) => teamEligibleForSeason(id, row.season, expansionNow))
                    .map((id) => (
                        <option key={id} value={id}>{pwhl(id).name}</option>
                    ))}
            </select>
        );
    }
    if (league === "ncaa") {
        return (
            <select
                value={row.team ?? ""}
                onChange={(e) => {
                    const v = e.target.value;
                    onChange(idx, "team", v);
                    const found = ALL_NCAA_SCHOOLS.find((s) => s.school === v);
                    if (found) onChange(idx, "league", found.conference);
                }}
                className={inputCls + " text-xs py-1.5 min-w-[200px]"}
                data-testid={`${dataTestPrefix}-team`}
            >
                <option value="">—</option>
                {NCAA_SCHOOL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{ncaa(o.value).name} ({o.label.match(/\(([^)]+)\)/)?.[1] || ""})</option>
                ))}
            </select>
        );
    }
    return (
        <input
            value={row.team ?? ""}
            onChange={(e) => onChange(idx, "team", e.target.value)}
            className={inputCls + " text-xs py-1.5"}
            data-testid={`${dataTestPrefix}-team`}
            placeholder="Country (e.g. Canada)"
        />
    );
};

// ---------- Season Block (sortable) ----------
function SeasonBlock({
    sortId,
    group,
    rows,
    isGoalie,
    league,
    seasonOpts,
    expansionNow,
    manualKeys,
    fullyEditable = false,
    hideStageButtons = false,
    classOptions = null,
    seasonClassValue,
    onSeasonClass,
    onUpdate,
    onRemoveRow,
    onAddTeamRow,
    onRemoveGroup,
    onChangeSeason,
    onUpdateCell,
    onChangeTeam,
    onRemoveTeam,
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    };
    const isPwhl = league === "pwhl";
    // PWHL uses the full stat-column set (matches public Player Profile); other
    // leagues keep the compact manual set.
    const regColsFinal = isPwhl ? pwhlStatCols(isGoalie) : (isGoalie ? GOALIE_REG_COLS : SKATER_REG_COLS);
    const totals = isPwhl
        ? sumByCols(group, rows, regColsFinal, isGoalie)
        : (isGoalie ? sumGoalie(group, rows) : sumSkater(group, rows));
    const teamRows = group.indices.map((i) => ({ idx: i, row: rows[i] }));
    const showTotal = teamRows.length > 1;
    // Whether a given PWHL column is editable for this player.
    const colEditable = (c) => isPwhl && !c.derived && (fullyEditable || (manualKeys || new Set()).has(c.key));

    // ---- Paste-from-Sheets schema ----
    // Header row expected by paste box: Team | Stage | GP | <stat cols...>
    const pasteSchema = [
        ["team", "Team", "text"],
        ["stage", "Stage", "text"],
        ...regColsFinal.map((c) => [c.key, c.label, "num"]),
    ];
    const onApplyPaste = (matrix) => {
        if (!matrix.length) return;
        const newRows = tsvToObjects(matrix, pasteSchema, {
            season: group.season || "",
            league: league.toUpperCase(),
            stage: "regular",
        });
        // Normalise stage values
        newRows.forEach((r) => {
            const s = String(r.stage || "regular").toLowerCase();
            r.stage = s.startsWith("pre") ? "preseason" : s.startsWith("play") || s === "po" ? "playoffs" : "regular";
        });
        // Insert below the last existing row of this season
        let insertAt = rows.length;
        for (let i = rows.length - 1; i >= 0; i--) {
            if (rows[i].season === group.season) { insertAt = i + 1; break; }
        }
        const next = [...rows];
        next.splice(insertAt, 0, ...newRows);
        // Reuse the existing parent setter via onChangeSeason? — not ideal; use
        // a direct callback we plumb in below via window.parent ref. The
        // SeasonsEditor's parent passes onRowsChange via context — but here we
        // only have onUpdate / onAddTeamRow. So expose a side-channel by
        // calling onAddTeamRow once per row, then patching values.
        // Simpler: call window's editor-level rows setter via the closest
        // ancestor through onAddTeamRow trick. Since this component lives
        // inside SeasonsEditor, we delegate via a custom event:
        const ev = new CustomEvent("seasons-editor-bulk-add", { detail: { league, rows: newRows } });
        window.dispatchEvent(ev);
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="rosterly-card border-l-4 border-l-[#9D4CDD] p-3"
            data-testid={`season-block-${league}-${group.season || "unset"}`}
        >
            <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-1">
                    <button
                        type="button"
                        {...attributes}
                        {...listeners}
                        className="cursor-grab active:cursor-grabbing text-[#94A3B8] hover:text-white"
                        data-testid={`drag-${league}-${group.season || "unset"}`}
                        aria-label="Drag to reorder season"
                    >
                        <GripVertical className="h-4 w-4" />
                    </button>
                    <select
                        value={group.season}
                        onChange={(e) => onChangeSeason(group.season, e.target.value)}
                        className={inputCls + " text-sm py-1.5 max-w-[140px]"}
                        data-testid={`season-select-${league}-${group.season || "unset"}`}
                    >
                        <option value="">— set season —</option>
                        {seasonOpts.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#64748B]">
                        {teamRows.length} team{teamRows.length !== 1 ? "s" : ""}
                    </span>
                    {classOptions && group.season && (
                        <select
                            value={seasonClassValue ? (seasonClassValue(group.season) || "") : ""}
                            onChange={(e) => onSeasonClass && onSeasonClass(group.season, e.target.value)}
                            className="text-[10px] uppercase tracking-wider font-bold bg-[#9D4CDD]/10 text-[#C99BF0] border border-[#9D4CDD]/30 rounded px-1.5 py-1"
                            data-testid={`class-year-select-${group.season}`}
                            title="NCAA class year"
                        >
                            {classOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    )}
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                    {(league === "pwhl" && !fullyEditable) ? (
                        <span
                            className="text-[10px] uppercase tracking-wider font-bold text-[#9D4CDD] bg-[#9D4CDD]/10 px-2 py-1 rounded border border-[#9D4CDD]/30"
                            title="Auto stats sync from Game Centre game logs. Columns marked Manual in Admin → Settings are editable here."
                            data-testid={`pwhl-auto-only-${group.season}`}
                        >
                            AUTO from games · Manual cols editable
                        </span>
                    ) : (<>
                    {!hideStageButtons && (<>
                    <button
                        type="button"
                        onClick={() => onAddTeamRow(group.season, "preseason")}
                        disabled={!group.season}
                        className="btn-secondary text-[10px] inline-flex items-center gap-1 disabled:opacity-40 uppercase tracking-wider"
                        data-testid={`add-preseason-row-${league}-${group.season || "unset"}`}
                    >
                        <Plus className="h-3 w-3" /> Preseason
                    </button>
                    <button
                        type="button"
                        onClick={() => onAddTeamRow(group.season, "regular")}
                        disabled={!group.season}
                        className="btn-secondary text-[10px] inline-flex items-center gap-1 disabled:opacity-40 uppercase tracking-wider"
                        data-testid={`add-team-row-${league}-${group.season || "unset"}`}
                    >
                        <Plus className="h-3 w-3" /> Team
                    </button>
                    <button
                        type="button"
                        onClick={() => onAddTeamRow(group.season, "playoffs")}
                        disabled={!group.season}
                        className="btn-secondary text-[10px] inline-flex items-center gap-1 disabled:opacity-40 uppercase tracking-wider text-[#FBBF24]"
                        data-testid={`add-playoff-row-${league}-${group.season || "unset"}`}
                    >
                        <Plus className="h-3 w-3" /> Playoff
                    </button>
                    </>)}
                    </>)}
                    {(league !== "pwhl" || fullyEditable) && (
                        <button
                            type="button"
                            onClick={() => onRemoveGroup(group.season)}
                            className="text-[#94A3B8] hover:text-[#EF4444] p-1"
                            data-testid={`remove-season-${league}-${group.season || "unset"}`}
                            title="Remove this season"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </div>
            {(league !== "pwhl" || fullyEditable) && (
                <div className="px-1 pt-1">
                    <PasteFromSheets
                        onApply={onApplyPaste}
                        columns={pasteSchema.map(([k, l]) => [k, l])}
                        testId={`${league}-${group.season || "unset"}-paste`}
                        buttonLabel="Paste rows from Google Sheets"
                    />
                </div>
            )}
            {!isPwhl ? (
                <MergedHistoryTable
                    group={group}
                    rows={rows}
                    isGoalie={isGoalie}
                    league={league}
                    expansionNow={expansionNow}
                    hideStageButtons={hideStageButtons}
                    onUpdateCell={onUpdateCell}
                    onChangeTeam={onChangeTeam}
                    onRemoveTeam={onRemoveTeam}
                    onAddTeamRow={onAddTeamRow}
                />
            ) : (
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-left text-[#64748B]">
                            <th className="pb-1 pr-1">Team</th>
                            <th className="pb-1 pr-1 text-center">Stage</th>
                            {league !== "international" && <th className="pb-1 pr-1 text-center">Lge</th>}
                            <th className="pb-1 pr-1 text-center" title="Auto-sync row from game logs (2026-27+ only)">Auto</th>
                            {regColsFinal.map((c, i) => (
                                <th key={c.key} className={`pb-1 px-0.5 text-center ${i === 0 ? "border-l border-[#1E2233] pl-1.5" : ""}`}>{c.label}</th>
                            ))}
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {teamRows.map(({ idx, row }) => {
                            const seasonStr = row.season || group.season || "";
                            // PWHL is fully auto-only across all seasons — manual entry is locked,
                            // UNLESS this is the fully-editable MANUAL dataset.
                            const autoEligible = league === "pwhl" && !fullyEditable;
                            // Any game-log-derived row (auto_synced) gets automatic
                            // team + stage (read-only) regardless of league.
                            const autoOn = autoEligible || row.auto_synced === true;
                            return (
                            <tr key={idx}>
                                <td className="pr-1 pb-1">
                                    <TeamCell
                                        league={league}
                                        row={row}
                                        idx={idx}
                                        onChange={onUpdate}
                                        dataTestPrefix={`${league}-${idx}`}
                                        expansionNow={expansionNow}
                                        readOnly={autoOn}
                                    />
                                </td>
                                <td className="pr-1 pb-1">
                                    {autoOn ? (
                                        <span
                                            className="pill bg-[#9D4CDD]/25 text-[#E9D5FF] text-[10px] uppercase tracking-wider"
                                            data-testid={`${league}-${idx}-stage`}
                                        >
                                            {row.stage === "playoffs" ? "PO" : row.stage === "preseason" ? "PRE" : "RS"}
                                        </span>
                                    ) : (
                                        <select
                                            value={row.stage || "regular"}
                                            onChange={(e) => onUpdate(idx, "stage", e.target.value)}
                                            className={inputCls + " text-[10px] py-1.5 uppercase tracking-wider"}
                                            data-testid={`${league}-${idx}-stage`}
                                            title="Stage"
                                        >
                                            <option value="preseason">Pre</option>
                                            <option value="regular">RS</option>
                                            <option value="playoffs">PO</option>
                                        </select>
                                    )}
                                </td>
                                {league !== "international" && (
                                    <td className="pr-1 pb-1">
                                        {league === "pwhl" ? (
                                            <span className="text-xs px-2 py-1.5 text-[#94A3B8] block">PWHL</span>
                                        ) : (
                                            <input
                                                value={row.league ?? ""}
                                                onChange={(e) => onUpdate(idx, "league", e.target.value)}
                                                className={inputCls + " text-xs py-1.5"}
                                                data-testid={`${league}-${idx}-league`}
                                            />
                                        )}
                                    </td>
                                )}
                                <td className="pr-1 pb-1">
                                    {autoEligible ? (
                                        <span
                                            title="Auto-synced from game logs. Add games via Admin → Schedule + Game Centre."
                                            className="inline-flex items-center text-[9px] uppercase tracking-wider font-bold px-2 py-1 rounded border bg-[#9D4CDD]/20 border-[#9D4CDD] text-[#E9D5FF]"
                                            data-testid={`${league}-${idx}-auto-synced`}
                                        >
                                            AUTO
                                        </span>
                                    ) : (
                                        <span className="text-[9px] uppercase tracking-wider text-[#64748B]" data-testid={`${league}-${idx}-auto-synced`}>—</span>
                                    )}
                                </td>
                                {regColsFinal.map((c, ci) => {
                                    const cls0 = ci === 0 ? "border-l border-[#1E2233] pl-1.5" : "";
                                    // PWHL derived ratio → read-only computed value.
                                    if (league === "pwhl" && c.derived) {
                                        const dv = computePwhlDerived(row, isGoalie)[c.key];
                                        return (
                                            <td key={c.key} className={`px-0.5 pb-1 text-center ${cls0}`}>
                                                <span className="block text-xs py-1 w-10 mx-auto text-center font-mono-stat text-[#64748B]" data-testid={`${league}-${idx}-${c.key}`}>
                                                    {fmtDerived(c.key, dv)}
                                                </span>
                                            </td>
                                        );
                                    }
                                    // PWHL: editable only when this column is Manual; otherwise auto-locked.
                                    const locked = league === "pwhl" ? !colEditable(c) : autoOn;
                                    if (locked) {
                                        // Read-only stat — render as plain text so multi-digit
                                        // totals show in full (a cramped number input clips them).
                                        return (
                                            <td key={c.key} className={`px-0.5 pb-1 text-center ${cls0}`}>
                                                <span className="block text-xs py-1 w-10 mx-auto text-center font-mono-stat text-[#94A3B8]" data-testid={`${league}-${idx}-${c.key}`}>
                                                    {row[c.key] ?? 0}
                                                </span>
                                            </td>
                                        );
                                    }
                                    return (
                                        <td key={c.key} className={`px-0.5 pb-1 text-center ${cls0}`}>
                                            <input
                                                value={row[c.key] ?? (c.text ? "" : 0)}
                                                onChange={(e) => onUpdate(idx, c.key, e.target.value)}
                                                type={c.text ? "text" : "number"}
                                                step={c.text ? undefined : (c.step || "1")}
                                                title={league === "pwhl" ? "Manual stat — type the season total" : undefined}
                                                className="bg-[#0A0B10] border border-[#9D4CDD]/50 focus:border-[#9D4CDD] outline-none text-white rounded text-xs py-1 px-1 w-10 mx-auto text-center font-mono-stat transition-colors"
                                                data-testid={`${league}-${idx}-${c.key}`}
                                            />
                                        </td>
                                    );
                                })}
                                <td>
                                    {autoOn ? (
                                        <span className="text-[9px] text-[#64748B]" title="Auto rows are deleted by removing their game logs.">—</span>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => onRemoveRow(idx)}
                                            className="text-[#94A3B8] hover:text-[#EF4444]"
                                            data-testid={`remove-row-${league}-${idx}`}
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    )}
                                </td>
                            </tr>
                            );
                        })}
                        {showTotal && (
                            <tr className="bg-[#0A0B10] border-t border-[#9D4CDD]/40" data-testid={`season-total-${league}-${group.season}`}>
                                <td className="pr-1 pb-1 pt-1 font-bold text-[#E9D5FF] uppercase tracking-wider text-[10px]">Total</td>
                                <td></td>
                                {league !== "international" && <td></td>}
                                <td></td>
                                {regColsFinal.map((c, ci) => (
                                    <td key={c.key} className={`px-0.5 pb-1 pt-1 text-center font-mono-stat font-bold text-[#E9D5FF] ${ci === 0 ? "border-l border-[#1E2233] pl-1.5" : ""}`}>
                                        {league === "pwhl" && c.derived ? fmtDerived(c.key, totals[c.key]) : formatStat(totals[c.key], c.step)}
                                    </td>
                                ))}
                                <td></td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            )}
        </div>
    );
}

// ---------- Merged History Table (non-PWHL, Elite-Prospects single-row) ----------
// Each (season, team) renders ONE row with Regular Season columns AND a gold
// Playoffs column group inline. Storage stays as separate regular / playoffs
// rows (stage field) so the public profile keeps reading them unchanged.
function MergedHistoryTable({ group, rows, isGoalie, league, expansionNow, hideStageButtons, onUpdateCell, onChangeTeam, onRemoveTeam, onAddTeamRow }) {
    const regCols = isGoalie ? GOALIE_REG_COLS : SKATER_REG_COLS;
    const isPo = (r) => (r.stage || "regular") === "playoffs";
    const season = group.season;
    // Team + league are LOCKED by default (synced data shouldn't be edited by
    // accident). "Manual Edit" unlocks them for that row.
    const [unlocked, setUnlocked] = useState({});
    const toggleLock = (t) => setUnlocked((u) => ({ ...u, [t]: !u[t] }));
    const indexed = group.indices.map((i) => rows[i]);
    const teamsInOrder = [];
    indexed.forEach((r) => { const t = r.team || ""; if (!teamsInOrder.includes(t)) teamsInOrder.push(t); });
    const mergedTeams = teamsInOrder.map((t) => ({
        team: t,
        regRow: indexed.find((r) => (r.team || "") === t && !isPo(r)) || null,
        poRow: indexed.find((r) => (r.team || "") === t && isPo(r)) || null,
    }));
    const showTotal = mergedTeams.length > 1;

    const skaterTotal = (list) => {
        const keys = regCols.map((c) => c.key);
        const t = Object.fromEntries(keys.map((k) => [k, 0]));
        list.forEach((r) => keys.forEach((k) => { t[k] += Number(r?.[k] || 0); }));
        return t;
    };
    const goalieTotal = (list) => {
        let gp = 0, w = 0, l = 0, tt = 0, otl = 0, so = 0, gaaSum = 0, svSum = 0;
        list.forEach((r) => { if (!r) return; const g = Number(r.gp || 0); gp += g; w += Number(r.w || 0); l += Number(r.l || 0); tt += Number(r.t || 0); otl += Number(r.otl || 0); so += Number(r.so || 0); gaaSum += Number(r.gaa || 0) * g; svSum += Number(r.sv_pct || 0) * g; });
        return { gp, w, l, t: tt, otl, so, gaa: gp ? gaaSum / gp : 0, sv_pct: gp ? svSum / gp : 0 };
    };
    const total = (list) => (isGoalie ? goalieTotal(list) : skaterTotal(list));
    const regTotal = total(mergedTeams.map((m) => m.regRow).filter(Boolean));
    const poTotal = total(mergedTeams.map((m) => m.poRow).filter(Boolean));

    const cellInput = (row, isPlayoff, team, c) => (
        <input
            value={row?.[c.key] ?? 0}
            onChange={(e) => onUpdateCell(season, team, isPlayoff, c.key, e.target.value)}
            type="number"
            step={c.step || "1"}
            className="bg-[#0A0B10] border border-[#1E2233] focus:border-[#9D4CDD] outline-none text-white rounded text-xs py-1 px-1 w-12 mx-auto text-center font-mono-stat transition-colors"
            data-testid={`hist-${league}-${team || "x"}-${isPlayoff ? "po" : "rs"}-${c.key}`}
        />
    );

    return (
        <div className="overflow-x-auto" data-testid={`merged-history-${league}-${season || "unset"}`}>
            <table className="w-full text-xs">
                <thead>
                    <tr className="text-left text-[#64748B]">
                        <th className="pb-1 pr-1" rowSpan={2}>Team</th>
                        {league !== "international" && <th className="pb-1 pr-1 text-center" rowSpan={2}>Lge</th>}
                        <th className="pb-1 px-1 text-center border-l border-[#1E2233] text-[#94A3B8] uppercase tracking-wider text-[10px]" colSpan={regCols.length}>Regular Season</th>
                        <th className="pb-1 px-1 text-center border-l border-[#1E2233] text-[#94A3B8] uppercase tracking-wider text-[10px]" colSpan={regCols.length}>Playoffs</th>
                        <th rowSpan={2}></th>
                    </tr>
                    <tr className="text-left text-[#64748B]">
                        {regCols.map((c, i) => (
                            <th key={`rsh-${c.key}`} className={`pb-1 px-0.5 text-center ${i === 0 ? "border-l border-[#1E2233] pl-1.5" : ""}`}>{c.label}</th>
                        ))}
                        {regCols.map((c, i) => (
                            <th key={`poh-${c.key}`} className={`pb-1 px-0.5 text-center ${i === 0 ? "border-l border-[#1E2233] pl-1.5" : ""}`}>{c.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {mergedTeams.map(({ team, regRow, poRow }) => {
                        const capLabel = (regRow || poRow || {}).captaincy || "";
                        const isUnlocked = !!unlocked[team];
                        return (
                        <tr key={team || "__blank__"} data-testid={`hist-row-${league}-${team || "x"}`}>
                            <td className="pr-1 pb-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <TeamCell
                                        league={league}
                                        row={regRow || poRow || { team }}
                                        idx={0}
                                        onChange={(_, key, v) => { if (key === "team") onChangeTeam(season, team, v); }}
                                        dataTestPrefix={`hist-${league}-${team || "x"}`}
                                        expansionNow={expansionNow}
                                        readOnly={!isUnlocked}
                                    />
                                    {capLabel && (
                                        <span className="pill bg-[#9D4CDD]/20 text-[#E9D5FF] border border-[#9D4CDD]/40 text-[10px] uppercase font-bold tracking-wider whitespace-nowrap" data-testid={`hist-${league}-${team || "x"}-captaincy`}>
                                            {capLabel}
                                        </span>
                                    )}
                                </div>
                            </td>
                            {league !== "international" && (
                                <td className="pr-1 pb-1">
                                    {isUnlocked ? (
                                        <input
                                            value={(regRow || poRow || {}).league ?? ""}
                                            onChange={(e) => onUpdateCell(season, team, false, "league", e.target.value)}
                                            className={inputCls + " text-xs py-1.5"}
                                            data-testid={`hist-${league}-${team || "x"}-league`}
                                        />
                                    ) : (
                                        <span className="text-xs text-[#E2E8F0] py-1.5 inline-block whitespace-nowrap" data-testid={`hist-${league}-${team || "x"}-league`}>
                                            {(regRow || poRow || {}).league || "—"}
                                        </span>
                                    )}
                                </td>
                            )}
                            {regCols.map((c, i) => (
                                <td key={`rs-${c.key}`} className={`px-0.5 pb-1 text-center ${i === 0 ? "border-l border-[#1E2233] pl-1.5" : ""}`}>
                                    {cellInput(regRow, false, team, c)}
                                </td>
                            ))}
                            {regCols.map((c, i) => (
                                <td key={`po-${c.key}`} className={`px-0.5 pb-1 text-center ${i === 0 ? "border-l border-[#1E2233] pl-1.5" : ""}`}>
                                    {cellInput(poRow, true, team, c)}
                                </td>
                            ))}
                            <td className="whitespace-nowrap">
                                <div className="flex items-center gap-1.5">
                                    <button
                                        type="button"
                                        onClick={() => toggleLock(team)}
                                        className={`text-[9px] uppercase tracking-wider font-bold px-2 py-1 rounded border transition-colors ${isUnlocked ? "bg-[#9D4CDD] border-[#9D4CDD] text-white" : "border-[#1E2233] text-[#94A3B8] hover:text-white hover:border-[#9D4CDD]/50"}`}
                                        data-testid={`hist-manual-edit-${league}-${team || "x"}`}
                                        title={isUnlocked ? "Lock team & league" : "Unlock team & league for manual editing"}
                                    >
                                        {isUnlocked ? "Lock" : "Manual Edit"}
                                    </button>
                                    <button type="button" onClick={() => onRemoveTeam(season, team)} className="text-[#94A3B8] hover:text-[#EF4444]" data-testid={`hist-remove-${league}-${team || "x"}`}>
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                        );
                    })}
                    {showTotal && (
                        <tr className="bg-[#0A0B10] border-t border-[#9D4CDD]/40" data-testid={`hist-total-${league}-${season}`}>
                            <td className="pr-1 pb-1 pt-1 font-bold text-[#E9D5FF] uppercase tracking-wider text-[10px]">Total</td>
                            {league !== "international" && <td></td>}
                            {regCols.map((c, i) => (
                                <td key={`tr-${c.key}`} className={`px-0.5 pb-1 pt-1 text-center font-mono-stat font-bold text-[#E9D5FF] ${i === 0 ? "border-l border-[#1E2233] pl-1.5" : ""}`}>{formatStat(regTotal[c.key], c.step)}</td>
                            ))}
                            {regCols.map((c, i) => (
                                <td key={`tp-${c.key}`} className={`px-0.5 pb-1 pt-1 text-center font-mono-stat font-bold text-[#E9D5FF] ${i === 0 ? "border-l border-[#1E2233] pl-1.5" : ""}`}>{formatStat(poTotal[c.key], c.step)}</td>
                            ))}
                            <td></td>
                        </tr>
                    )}
                </tbody>
            </table>
            {!hideStageButtons && (
                <div className="pt-2">
                    <button
                        type="button"
                        onClick={() => onAddTeamRow(season, "regular")}
                        disabled={!season}
                        className="btn-secondary text-[10px] inline-flex items-center gap-1 disabled:opacity-40 uppercase tracking-wider"
                        data-testid={`hist-add-team-${league}-${season || "unset"}`}
                    >
                        <Plus className="h-3 w-3" /> Add team
                    </button>
                </div>
            )}
        </div>
    );
}

// ---------- Main editor ----------
export const SeasonsEditor = ({ title, league, isGoalie, rows, onRowsChange, expansionNow = false, fullyEditable = false, hideStageButtons = false, classOptions = null, seasonClassValue, onSeasonClass }) => {
    const seasonOpts = STAT_SEASON_OPTIONS[league] || STAT_SEASON_OPTIONS.pwhl;
    const groups = useMemo(() => groupBySeasonOrdered(rows || []), [rows]);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
    const regColsFinal = league === "pwhl" ? pwhlStatCols(isGoalie) : (isGoalie ? GOALIE_REG_COLS : SKATER_REG_COLS);

    // Global PWHL Stat Mode config (Admin → Settings): which stat keys are manual.
    const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: getSettings, enabled: league === "pwhl" });
    const manualKeys = useMemo(() => new Set(settings?.pwhl_manual_stats || []), [settings]);

    // NCAA schools pasted in stat rows that didn't resolve to a known school —
    // surfaced so the admin can map them once (mapping is then remembered).
    const unmatchedNcaa = useMemo(() => {
        if (league !== "ncaa") return [];
        const set = new Set();
        (rows || []).forEach((r) => {
            const t = String(r.team || "").trim();
            if (t && !isKnownNcaaSchool(t)) set.add(t);
        });
        return [...set];
    }, [league, rows]);

    const mapUnmatchedSchool = (raw, school) => {
        if (!school) return;
        saveSchoolAlias(raw, school);
        const conf = ALL_NCAA_SCHOOLS.find((s) => s.school === school)?.conference || "";
        onRowsChange((rows || []).map((r) =>
            String(r.team || "").trim() === raw
                ? { ...r, team: school, ...(conf ? { league: conf } : {}) }
                : r
        ));
    };

    // ---- Editor-level Paste-from-Sheets (Season-aware) ----
    // Schema for skaters: Season | Stage | Team | GP | G | A | PTS | PIM | +/-
    // Schema for goalies: Season | Stage | Team | GP | W | L | OTL | GAA | SV% | SO
    // (Team is a team_id "bos" or display name "Boston Fleet" — normalised below.)
    const editorPasteSchema = [
        ["season", "Season", "text"],
        ["stage", "Stage", "text"],
        ["team", "Team", "text"],
        ...regColsFinal.map((c) => [c.key, c.label, "num"]),
    ];

    const _normTeamForLeague = (raw) => {
        const v = String(raw || "").trim();
        if (!v) return "";
        if (league === "pwhl") {
            const key = v.toLowerCase();
            if (TEAM_INFO[key]) return key;
            const found = Object.entries(TEAM_INFO).find(([id, info]) =>
                info.name?.toLowerCase() === key || info.abbr?.toLowerCase() === key || id === key
            );
            return found ? found[0] : v;
        }
        if (league === "ncaa") {
            // Tolerant matcher: strips captain "A"/"C" markers and Univ./University
            // abbreviations, honours admin-saved aliases. Falls back to the raw
            // string (surfaced for manual mapping below) when unmatched.
            return matchNcaaSchool(v) || v;
        }
        return v; // international: country string as-is
    };
    const _normStage = (raw) => {
        const s = String(raw || "regular").toLowerCase().trim();
        if (s.startsWith("pre")) return "preseason";
        if (s.startsWith("play") || s === "po") return "playoffs";
        return "regular";
    };

    const onEditorPasteApply = (matrix) => {
        if (!matrix.length) return;
        const newRows = tsvToObjects(matrix, editorPasteSchema, {
            league: league === "pwhl" ? "PWHL" : (league === "ncaa" ? "" : ""),
            auto_synced: true,
        });
        newRows.forEach((r) => {
            r.stage = _normStage(r.stage);
            r.team = _normTeamForLeague(r.team);
            // Auto P = G+A for skaters when P missing/zero
            if (!isGoalie && (!r.p || Number(r.p) === 0)) {
                r.p = (Number(r.g) || 0) + (Number(r.a) || 0);
            }
            if (league === "ncaa" && r.team) {
                const match = ALL_NCAA_SCHOOLS.find((s) => s.school === r.team);
                if (match && !r.league) r.league = match.conference;
            }
        });
        onRowsChange([...(rows || []), ...newRows]);
    };

    // Listen for bulk-paste events from descendant SeasonBlocks (legacy per-block paste).
    useEffect(() => {
        const handler = (ev) => {
            const detail = ev.detail || {};
            if (detail.league !== league) return;
            if (!Array.isArray(detail.rows) || !detail.rows.length) return;
            onRowsChange([...(rows || []), ...detail.rows]);
        };
        window.addEventListener("seasons-editor-bulk-add", handler);
        return () => window.removeEventListener("seasons-editor-bulk-add", handler);
    }, [league, rows, onRowsChange]);

    // Stable sortable IDs — group index as id
    const sortIds = groups.map((g, i) => `${g.season || "__unset__"}-${i}`);

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIdx = sortIds.indexOf(active.id);
        const newIdx = sortIds.indexOf(over.id);
        if (oldIdx < 0 || newIdx < 0) return;
        const newGroupOrder = arrayMove(groups, oldIdx, newIdx);
        const flat = [];
        for (const g of newGroupOrder) {
            for (const idx of g.indices) flat.push(rows[idx]);
        }
        onRowsChange(flat);
    };

    const addNewSeason = () => {
        const blank = isGoalie ? blankGoalieRow() : blankSkaterRow();
        blank.season = "";
        // Insert at top
        onRowsChange([blank, ...(rows || [])]);
    };

    const addTeamRow = (season, stage = "regular") => {
        const blank = isGoalie ? blankGoalieRow() : blankSkaterRow();
        blank.season = season;
        blank.stage = stage;
        // Insert right after last row of this season
        let insertAt = rows.length;
        for (let i = rows.length - 1; i >= 0; i--) {
            if (rows[i].season === season) { insertAt = i + 1; break; }
        }
        const next = [...rows];
        next.splice(insertAt, 0, blank);
        onRowsChange(next);
    };

    const removeRow = (idx) => {
        onRowsChange(rows.filter((_, i) => i !== idx));
    };

    const updateRow = (idx, key, value) => {
        const next = rows.map((r, i) => {
            if (i !== idx) return r;
            const updated = { ...r, [key]: value };
            // Auto-derive points: P = G + A (only if user is editing g or a, not p directly)
            if (!isGoalie && (key === "g" || key === "a")) {
                updated.p = (Number(updated.g) || 0) + (Number(updated.a) || 0);
            }
            if (!isGoalie && (key === "po_g" || key === "po_a")) {
                updated.po_p = (Number(updated.po_g) || 0) + (Number(updated.po_a) || 0);
            }
            return updated;
        });
        onRowsChange(next);
    };

    const removeGroup = (season) => {
        if (!window.confirm(`Remove ALL team rows for "${season || "this unset season"}"?`)) return;
        onRowsChange(rows.filter((r) => r.season !== season));
    };

    const changeSeason = (oldSeason, newSeason) => {
        onRowsChange(rows.map((r) => r.season === oldSeason ? { ...r, season: newSeason } : r));
    };

    // ---- Merged (Elite-Prospects) history handlers — non-PWHL only ----
    const _blankFor = (season, team, stage) => {
        const blank = isGoalie ? blankGoalieRow() : blankSkaterRow();
        blank.season = season; blank.team = team; blank.stage = stage;
        blank.league = league === "ncaa"
            ? (ALL_NCAA_SCHOOLS.find((s) => s.school === team)?.conference || "")
            : "";
        return blank;
    };
    const updateCell = (season, team, isPlayoff, key, value) => {
        let found = false;
        let next = rows.map((r) => {
            const match = r.season === season && (r.team || "") === (team || "") && ((r.stage || "regular") === "playoffs") === isPlayoff;
            if (match && !found) {
                found = true;
                const u = { ...r, [key]: value };
                if (!isGoalie && (key === "g" || key === "a")) u.p = (Number(u.g) || 0) + (Number(u.a) || 0);
                return u;
            }
            return r;
        });
        if (!found) {
            const blank = _blankFor(season, team, isPlayoff ? "playoffs" : "regular");
            blank[key] = value;
            if (!isGoalie && (key === "g" || key === "a")) blank.p = (Number(blank.g) || 0) + (Number(blank.a) || 0);
            next = [...rows, blank];
        }
        onRowsChange(next);
    };
    const changeTeam = (season, oldTeam, newTeam) => {
        const conf = league === "ncaa" ? (ALL_NCAA_SCHOOLS.find((s) => s.school === newTeam)?.conference || null) : null;
        onRowsChange(rows.map((r) => (r.season === season && (r.team || "") === (oldTeam || ""))
            ? { ...r, team: newTeam, ...(conf ? { league: conf } : {}) }
            : r));
    };
    const removeTeam = (season, team) => {
        if (!window.confirm(`Remove ${team || "this team"}'s rows for ${season || "this season"}?`)) return;
        onRowsChange(rows.filter((r) => !(r.season === season && (r.team || "") === (team || ""))));
    };

    return (
        <div data-testid={`stat-editor-${league}`}>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                {title && <h4 className="font-heading text-lg font-bold uppercase tracking-tight">{title}</h4>}
                {(league !== "pwhl" || fullyEditable) && (
                    <button
                        type="button"
                        onClick={addNewSeason}
                        className="btn-secondary text-xs inline-flex items-center gap-1"
                        data-testid={`add-${league}-season`}
                    >
                        <Plus className="h-3 w-3" /> Add season
                    </button>
                )}
            </div>
            {(league === "pwhl" && !fullyEditable) ? (
                <div className="rosterly-card p-3 border border-[#9D4CDD]/30 bg-[#9D4CDD]/5 mb-3" data-testid={`pwhl-auto-banner-${isGoalie ? "goalie" : "skater"}`}>
                    <p className="text-[11px] uppercase tracking-wider font-bold text-[#E9D5FF] mb-1">PWHL stats — auto-synced</p>
                    <p className="text-[11px] text-[#94A3B8] leading-relaxed">
                        These columns sync from <span className="text-white font-bold">Game Centre → Game Logs</span> and are locked here. Ratios (PTS, GAA, SV%, WF%, SH%, SO%) are always computed.
                    </p>
                </div>
            ) : (
                <div className="rosterly-card p-3 border border-[#1E2233] mb-3" data-testid={`paste-from-sheets-${league}-${isGoalie ? "goalie" : "skater"}`}>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-[#9D4CDD] mb-1">
                        {isGoalie ? "Goalie" : "Skater"} Stats Paste-from-Sheets
                    </p>
                    <p className="text-[11px] text-[#94A3B8] mb-2">
                        Columns: <span className="text-white font-mono-stat">{editorPasteSchema.map(([, l]) => l).join(" · ")}</span>.
                        Stage values: <code>preseason</code> / <code>regular</code> / <code>playoffs</code>.
                    </p>
                    <PasteFromSheets
                        onApply={onEditorPasteApply}
                        columns={editorPasteSchema.map(([k, l]) => [k, l])}
                        testId={`editor-paste-${league}`}
                        buttonLabel="Paste season rows from Google Sheets"
                    />
                </div>
            )}
            {unmatchedNcaa.length > 0 && (
                <div className="rosterly-card p-3 border border-[#FBBF24]/40 bg-[#FBBF24]/[0.06] mb-3" data-testid="ncaa-unmatched-schools">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-[#FBBF24] mb-1">
                        Unrecognised {unmatchedNcaa.length === 1 ? "school" : "schools"} — map once to remember
                    </p>
                    <p className="text-[11px] text-[#94A3B8] mb-2">
                        These pasted names didn't match an NCAA school. Pick the correct one and it will auto-match on every future import.
                    </p>
                    <div className="space-y-2">
                        {unmatchedNcaa.map((raw) => (
                            <div key={raw} className="flex items-center gap-2 flex-wrap" data-testid={`ncaa-unmatched-row-${raw}`}>
                                <span className="font-mono-stat text-xs text-white bg-[#0A0B10] border border-[#1E2233] rounded px-2 py-1">{raw}</span>
                                <span className="text-[#64748B]">→</span>
                                <select
                                    className={inputCls + " max-w-xs"}
                                    defaultValue=""
                                    onChange={(e) => mapUnmatchedSchool(raw, e.target.value)}
                                    data-testid={`ncaa-unmatched-select-${raw}`}
                                >
                                    <option value="">Select NCAA school…</option>
                                    {NCAA_SCHOOL_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {groups.length === 0 ? (
                <p className="text-xs text-[#64748B] italic">No {title.toLowerCase()} entered.</p>
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={sortIds} strategy={verticalListSortingStrategy}>
                        <div className="space-y-3">
                            {groups.map((g, i) => (
                                <SeasonBlock
                                    key={sortIds[i]}
                                    sortId={sortIds[i]}
                                    group={g}
                                    rows={rows}
                                    isGoalie={isGoalie}
                                    league={league}
                                    seasonOpts={seasonOpts}
                                    expansionNow={expansionNow}
                                    manualKeys={manualKeys}
                                    fullyEditable={fullyEditable}
                                    hideStageButtons={hideStageButtons}
                                    classOptions={classOptions}
                                    seasonClassValue={seasonClassValue}
                                    onSeasonClass={onSeasonClass}
                                    onUpdate={updateRow}
                                    onRemoveRow={removeRow}
                                    onAddTeamRow={addTeamRow}
                                    onRemoveGroup={removeGroup}
                                    onChangeSeason={changeSeason}
                                    onUpdateCell={updateCell}
                                    onChangeTeam={changeTeam}
                                    onRemoveTeam={removeTeam}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}
        </div>
    );
};

// Row factories (kept here to avoid circular imports)
const blankSkaterRow = () => ({
    season: "", team: "", league: "PWHL", stage: "regular", auto_synced: true,
    gp: 0, g: 0, a: 0, p: 0, pim: 0, plus_minus: 0, blk: 0, hits: 0, toi: "",
    po_gp: 0, po_g: 0, po_a: 0, po_p: 0, po_pim: 0, po_plus_minus: 0,
});
const blankGoalieRow = () => ({
    season: "", team: "", league: "PWHL", stage: "regular", auto_synced: true,
    gp: 0, w: 0, l: 0, t: 0, otl: 0, gaa: 0, sv_pct: 0, so: 0,
    po_gp: 0, po_w: 0, po_l: 0, po_t: 0, po_otl: 0, po_gaa: 0, po_sv_pct: 0, po_so: 0,
});

export default SeasonsEditor;
